import { supabase } from '@/lib/supabase';

export interface ScribeGuide {
  id: string;
  organization_id: string | null;
  url: string;
  title: string;
  description?: string;
  category?: string;
  tags?: string[];
  role: 'all' | 'admin' | 'staff';
  view_mode: 'slides' | 'movie' | 'scroll';
  is_featured: boolean;
  is_active: boolean;
  is_global: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface ScribeGuideBookmark {
  id: string;
  guide_id: string;
  user_id: string;
  created_at: string;
}

export interface CreateScribeGuideData {
  organization_id?: string | null;
  url: string;
  title: string;
  description?: string;
  category?: string;
  tags?: string[];
  role?: 'all' | 'admin' | 'staff';
  view_mode?: 'slides' | 'movie' | 'scroll';
  is_featured?: boolean;
  is_active?: boolean;
  is_global?: boolean;
  sort_order?: number;
}

export interface UpdateScribeGuideData {
  url?: string;
  title?: string;
  description?: string;
  category?: string;
  tags?: string[];
  role?: 'all' | 'admin' | 'staff';
  view_mode?: 'slides' | 'movie' | 'scroll';
  is_featured?: boolean;
  is_active?: boolean;
  is_global?: boolean;
  sort_order?: number;
}

export const ScribeGuideService = {
  /**
   * Get all guides accessible to the current user
   */
  async getGuides(organizationId?: string): Promise<ScribeGuide[]> {
    try {
      let query = supabase
        .from('scribe_guides')
        .select('*')
        .eq('is_active', true);

      // If organization ID is provided, include org-specific guides
      if (organizationId) {
        query = query.or(`is_global.eq.true,organization_id.eq.${organizationId}`);
      } else {
        // If no org ID, only show global guides
        query = query.eq('is_global', true);
      }

      const { data, error } = await query.order('sort_order', { ascending: true });

      if (error) {
        console.error('Error fetching scribe guides:', error);
        throw error;
      }

      return data || [];
    } catch (error) {
      console.error('Error in getGuides:', error);
      throw error;
    }
  },

  /**
   * Get guides by category
   */
  async getGuidesByCategory(category: string, organizationId?: string): Promise<ScribeGuide[]> {
    try {
      let query = supabase
        .from('scribe_guides')
        .select('*')
        .eq('is_active', true)
        .eq('category', category);

      if (organizationId) {
        query = query.or(`is_global.eq.true,organization_id.eq.${organizationId}`);
      } else {
        query = query.eq('is_global', true);
      }

      const { data, error } = await query.order('sort_order', { ascending: true });

      if (error) {
        console.error('Error fetching guides by category:', error);
        throw error;
      }

      return data || [];
    } catch (error) {
      console.error('Error in getGuidesByCategory:', error);
      throw error;
    }
  },

  /**
   * Search guides by title, description, or tags
   */
  async searchGuides(searchTerm: string, organizationId?: string): Promise<ScribeGuide[]> {
    try {
      let query = supabase
        .from('scribe_guides')
        .select('*')
        .eq('is_active', true);

      if (organizationId) {
        query = query.or(`is_global.eq.true,organization_id.eq.${organizationId}`);
      } else {
        query = query.eq('is_global', true);
      }

      // Search in title, description, and tags
      query = query.or(`title.ilike.%${searchTerm}%,description.ilike.%${searchTerm}%,tags.cs.{${searchTerm}}`);

      const { data, error } = await query.order('sort_order', { ascending: true });

      if (error) {
        console.error('Error searching guides:', error);
        throw error;
      }

      return data || [];
    } catch (error) {
      console.error('Error in searchGuides:', error);
      throw error;
    }
  },

  /**
   * Get a single guide by ID
   */
  async getGuideById(id: string): Promise<ScribeGuide | null> {
    try {
      const { data, error } = await supabase
        .from('scribe_guides')
        .select('*')
        .eq('id', id)
        .eq('is_active', true)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          return null; // No rows found
        }
        console.error('Error fetching guide by ID:', error);
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Error in getGuideById:', error);
      throw error;
    }
  },

  /**
   * Create a new guide (admin only)
   */
  async createGuide(guideData: CreateScribeGuideData): Promise<ScribeGuide> {
    try {
      const { data, error } = await supabase
        .from('scribe_guides')
        .insert([guideData])
        .select()
        .single();

      if (error) {
        console.error('Error creating guide:', error);
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Error in createGuide:', error);
      throw error;
    }
  },

  /**
   * Update a guide (admin only)
   */
  async updateGuide(id: string, updateData: UpdateScribeGuideData): Promise<ScribeGuide> {
    try {
      const { data, error } = await supabase
        .from('scribe_guides')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('Error updating guide:', error);
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Error in updateGuide:', error);
      throw error;
    }
  },

  /**
   * Delete a guide (admin only)
   */
  async deleteGuide(id: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('scribe_guides')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Error deleting guide:', error);
        throw error;
      }
    } catch (error) {
      console.error('Error in deleteGuide:', error);
      throw error;
    }
  },

  /**
   * Soft delete a guide by setting is_active to false
   */
  async deactivateGuide(id: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('scribe_guides')
        .update({ is_active: false })
        .eq('id', id);

      if (error) {
        console.error('Error deactivating guide:', error);
        throw error;
      }
    } catch (error) {
      console.error('Error in deactivateGuide:', error);
      throw error;
    }
  },

  /**
   * Get user's bookmarked guides
   */
  async getUserBookmarks(userId: string, organizationId?: string): Promise<ScribeGuide[]> {
    try {
      let query = supabase
        .from('scribe_guide_bookmarks')
        .select(`
          guide_id,
          scribe_guides!inner (*)
        `)
        .eq('user_id', userId)
        .eq('scribe_guides.is_active', true);

      if (organizationId) {
        // Filter for guides that are either global OR belong to the organization
        query = query.or(`is_global.eq.true,organization_id.eq.${organizationId}`, { foreignTable: 'scribe_guides' });
      } else {
        query = query.eq('scribe_guides.is_global', true);
      }

      const { data, error } = await query.order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching user bookmarks:', error);
        throw error;
      }

      // Extract the guides from the joined data
      return data?.map(item => item.scribe_guides as ScribeGuide) || [];
    } catch (error) {
      console.error('Error in getUserBookmarks:', error);
      throw error;
    }
  },

  /**
   * Check if a guide is bookmarked by the user
   */
  async isGuideBookmarked(guideId: string, userId: string): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .from('scribe_guide_bookmarks')
        .select('id')
        .eq('guide_id', guideId)
        .eq('user_id', userId)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          return false; // No rows found
        }
        console.error('Error checking bookmark:', error);
        throw error;
      }

      return !!data;
    } catch (error) {
      console.error('Error in isGuideBookmarked:', error);
      throw error;
    }
  },

  /**
   * Bookmark a guide
   */
  async bookmarkGuide(guideId: string, userId: string): Promise<ScribeGuideBookmark> {
    try {
      const { data, error } = await supabase
        .from('scribe_guide_bookmarks')
        .insert([{ guide_id: guideId, user_id: userId }])
        .select()
        .single();

      if (error) {
        console.error('Error bookmarking guide:', error);
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Error in bookmarkGuide:', error);
      throw error;
    }
  },

  /**
   * Remove bookmark from a guide
   */
  async unbookmarkGuide(guideId: string, userId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('scribe_guide_bookmarks')
        .delete()
        .eq('guide_id', guideId)
        .eq('user_id', userId);

      if (error) {
        console.error('Error unbookmarking guide:', error);
        throw error;
      }
    } catch (error) {
      console.error('Error in unbookmarkGuide:', error);
      throw error;
    }
  },

  /**
   * Get featured guides
   */
  async getFeaturedGuides(organizationId?: string): Promise<ScribeGuide[]> {
    try {
      let query = supabase
        .from('scribe_guides')
        .select('*')
        .eq('is_active', true)
        .eq('is_featured', true);

      if (organizationId) {
        query = query.or(`is_global.eq.true,organization_id.eq.${organizationId}`);
      } else {
        query = query.eq('is_global', true);
      }

      const { data, error } = await query.order('sort_order', { ascending: true });

      if (error) {
        console.error('Error fetching featured guides:', error);
        throw error;
      }

      return data || [];
    } catch (error) {
      console.error('Error in getFeaturedGuides:', error);
      throw error;
    }
  },

  /**
   * Get unique categories
   */
  async getCategories(organizationId?: string): Promise<string[]> {
    try {
      let query = supabase
        .from('scribe_guides')
        .select('category')
        .eq('is_active', true)
        .not('category', 'is', null);

      if (organizationId) {
        query = query.or(`is_global.eq.true,organization_id.eq.${organizationId}`);
      } else {
        query = query.eq('is_global', true);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching categories:', error);
        throw error;
      }

      // Extract unique categories
      const categories = [...new Set(data?.map(item => item.category).filter(Boolean))] as string[];
      return categories.sort();
    } catch (error) {
      console.error('Error in getCategories:', error);
      throw error;
    }
  }
};

export default ScribeGuideService;